import React, { useCallback, useEffect } from 'react'

import _ from 'lodash'
import { toast } from 'react-toastify'

import { WRAPPED_GAS_TOKENS, getChainNameFromChainId } from '@src/configs/chainInfo'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { LiquiditySource, ResultCode } from '@src/enums'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
// Removed useServerlessGasEstimation import as we now receive the values as parameters
import { isFeatureEnabled } from '@src/utils'
import { Balance } from '@src/utils/balance'
import { isFeatherChain } from '@src/utils/chainUtils'
import { extractMachFee } from '@src/utils/exchangeHelpers'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { createContextLogger } from '@src/utils/logger'
import { formatNumberStringWithCommas, returnSanitizedSellAmount } from '@src/utils/sanitizeSellAmount'
import { useTokensStore } from '@src/zustand_stores'

const logger = createContextLogger('useExchangeAmountLogic')

/**
 * Props for useExchangeAmountLogic
 */
interface UseExchangeAmountLogicProps {
    isGasEstimationFetched: boolean
    totalCostInGasToken: bigint
}

/**
 * Manages sellAmount, buyAmount, and quote logic.
 */
export function useExchangeAmountLogic({ isGasEstimationFetched, totalCostInGasToken }: UseExchangeAmountLogicProps) {
    const {
        // Everything from your local exchange state (Zustand or context).
        sellAmountString,
        isInsufficientBalance,
        isBuyOutputLoading,
        isQuoteInvalid,
        isManuallyRefreshing,
        liquiditySource,
        isQuoteEnabled,
        setIsQuoteEnabled,
        setSellAmountString,
        setIsInsufficientBalance,
        setIsBuyOutputLoading,
        setIsQuoteInvalid,
        setIsManuallyRefreshing,
        setLiquiditySource,
        setIsOrderRejected,
        setMachFee,
    } = useExchangeState()

    const {
        sellData,
        buyData,
        refetchQuote,
        isQuoteLoading,
        invalidateQuoteAndRefetch,
        mainWalletAddress,
        updateSellData,
        updateBuyData,
        quoteResponse,
        destinationAddress,
    } = useExchangeDataContext()

    const { sellBalance, wrappedNativeBalance, nativeBalance } = useExchangeBalances()

    const getGasTokenProps = useTokensStore(state => state.getGasTokenProps)

    const { setHasInsufficientGas, setGasAmountNeededString } = useExchangeState()

    useEffect(() => {
        const formatted = new Balance(sellData.token)
            .setValue(sellData.value)
            .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)
        const amountIsCorrect = sellAmountString === formatted
        if (!amountIsCorrect) {
            logger.info(
                `⛔️ [checkSellAmount] Quote disabled. Reason:\nsellAmountString="${sellAmountString}" !== formatted string="${formatted}"`
            )
        } else {
            logger.success('[checkSellAmount] setting isQuoteEnabled to true.')
        }
        setIsQuoteEnabled(amountIsCorrect)

        setTimeout(() => {
            logger.info('[checkSellAmount] Calling refetchQuote()...')
            refetchQuote()
        }, 500)
    }, [sellAmountString, sellData.value, sellData.token.decimals])

    /**
     * Debounced function that sets the final BigInt sell value.
     */
    const debouncedUpdateSellValue = _.debounce((bigintValue: bigint) => {
        // logger.info('[debouncedUpdateSellValue] Updating sell value to ', bigintValue.toString())
        updateSellData({ value: bigintValue })
    }, 500)

    // Update loading state based on query status
    useEffect(() => {
        // Only update loading state from query status if not a Feather order
        const isFeatherOrder = isFeatherChain(buyData.chainId)
        if (!isFeatherOrder) {
            setIsBuyOutputLoading(isQuoteEnabled && isQuoteLoading)
        }
    }, [isQuoteLoading, buyData.chainId])

    // Set loading to false if quote is not enabled
    useEffect(() => {
        if (!isQuoteEnabled) {
            setIsBuyOutputLoading(false)
        }
    }, [isQuoteEnabled])

    /**
     * Handle raw input changes, then use debounce to update the actual store state.
     */
    const handleOnChangeSellValue = useCallback(
        (input: string) => {
            try {
                const bigintValue = new Balance(sellData.token).parseString(input).bigintValue
                // logger.info('[handleOnChangeSellValue] input =', input, 'bigintValue=', bigintValue)

                if (!input || bigintValue === BigInt(0)) {
                    // logger.info(`[handleOnChangeSellValue] bigintValue =${bigintValue} Updating sellData...`)
                    updateSellData({ value: BigInt(0) })
                    updateBuyData({ value: BigInt(0) }) // Always clear buy value when sell is zero
                    setIsBuyOutputLoading(false)
                    setIsInsufficientBalance(false)
                    return
                }

                const isSellBalLessThanCurrentVal = sellBalance ? sellBalance.bigintValue < bigintValue : false
                // logger.info('[handleOnChangeSellValue] isSellBalLessThanCurrentVal =', isSellBalLessThanCurrentVal)
                setIsInsufficientBalance(isSellBalLessThanCurrentVal)

                // Handle Feather orders - set loading state for UI only when necessary
                const isFeatherOrder = isFeatherChain(buyData.chainId)
                if (isFeatherOrder && !isBuyOutputLoading) {
                    // Only update the loading state - don't clear buy value in this handler
                    // since it will be handled by the useFeatherOrder hook when needed
                    setIsBuyOutputLoading(true)
                    logger.info('[handleOnChangeSellValue] Feather order detected, showing loading state')
                }

                // Update the sell value with debounce
                debouncedUpdateSellValue(bigintValue)
            } catch (e) {
                logger.error('[handleOnChangeSellValue] error:', e)
            }
        },
        [sellData.token, buyData.chainId, isBuyOutputLoading]
    )

    /**
     * UI event handler for text input changes.
     */
    const onChangeSellValue = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            // logger.info('[onChangeSellValue] called.')

            const sanitized = returnSanitizedSellAmount(e.target.value, sellData.token.decimals)
            // logger.info('[onChangeSellValue] sanitized = ', sanitized)
            if (sanitized == undefined) {
                return // Avoid clearing input when the user presses a non-numeric button by mistake.
            }

            if (sanitized !== sellAmountString) {
                setSellAmountString(formatNumberStringWithCommas(sanitized) || '')

                if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
                    // logger.info(
                    //     'Setting sell value = buy value. Reson: ',
                    //     `isWrapOnly(sellData, buyData)=${isWrapOnly(sellData, buyData)}`,
                    //     `isUnWrapOnly(sellData, buyData)=${isUnWrapOnly(sellData, buyData)}`
                    // )
                    const bigintValue = new Balance(sellData.token).parseString(sanitized || '').bigintValue
                    updateSellData({ value: bigintValue })
                    updateBuyData({ value: bigintValue })
                    setIsBuyOutputLoading(false)

                    if (sellBalance && sellBalance.bigintValue < bigintValue) {
                        setIsInsufficientBalance(true)
                    } else {
                        setIsInsufficientBalance(false)
                    }
                    return
                }

                debouncedUpdateSellValue.cancel()

                if (!sanitized) {
                    handleOnChangeSellValue('')
                } else {
                    handleOnChangeSellValue(sanitized)
                }
            }
        },
        [
            handleOnChangeSellValue,
            debouncedUpdateSellValue,
            sellAmountString,
            sellData,
            buyData,
            sellBalance ? sellBalance.bigintValue : BigInt(0),
        ]
    )

    /**
     * Trigger a manual refresh of the quote.
     */
    const handleManualRefresh = useCallback(async () => {
        if (isManuallyRefreshing || !sellData.value || Number(sellData.value) === 0 || sellAmountString === '0') return
        setIsManuallyRefreshing(true)
        try {
            await refetchQuote()
        } finally {
            setIsManuallyRefreshing(false)
        }
    }, [isManuallyRefreshing, refetchQuote, sellData.value, sellAmountString])

    /**
     * Set max amount logic
     */
    const setMax = useCallback(
        (percentageAmount?: number) => {
            // logger.info('[setMax] function called with percentageAmount:', percentageAmount)
            if (sellBalance === undefined || sellBalance.bigintValue === BigInt(0)) {
                setSellAmountString('')
                updateSellData({ value: BigInt(0) })
                updateBuyData({ value: BigInt(0) })
                setIsInsufficientBalance(false)
                setIsBuyOutputLoading(false)
                return
            }

            setIsBuyOutputLoading(true)
            setLiquiditySource(LiquiditySource.unavailable)

            try {
                // Start with full balance
                let maxAmount = sellBalance.bigintValue

                // Apply percentage if specified (between 0-100)
                if (percentageAmount !== undefined && percentageAmount > 0 && percentageAmount <= 100) {
                    // Convert percentage to a decimal fraction
                    const percentageFraction = percentageAmount / 100

                    // Calculate the amount as percentage of balance using BigInt math
                    // Multiply by 10000 for precision, then divide later
                    const scaleFactor = BigInt(10000)
                    const scaledPercentage = BigInt(Math.floor(percentageFraction * 10000))
                    maxAmount = (maxAmount * scaledPercentage) / scaleFactor

                    logger.info(`Setting max amount to ${percentageAmount}% of balance: ${maxAmount.toString()}`)
                }

                // For native tokens, we need to reserve some balance for gas fees
                if (sellData.token.native && isGasEstimationFetched) {
                    // Check if we have enough balance for the gas fees
                    if (totalCostInGasToken > maxAmount) {
                        // Insufficient gas for transaction
                        logger.info(
                            '[setMax] Insufficient gas for transaction. Required:',
                            totalCostInGasToken.toString()
                        )
                        setHasInsufficientGas(true)

                        // Format the gas amount needed string (using the same function as in useServerlessGasEstimation)
                        const gasTokenProps = getGasTokenProps(sellData.chainId)
                        const gasAmountNeeded = `${new Balance(gasTokenProps).setValue(totalCostInGasToken).formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)} ${gasTokenProps.symbol}`
                        setGasAmountNeededString(gasAmountNeeded)

                        // Reset all values
                        updateBuyData({ value: BigInt(0) })
                        setSellAmountString('0')
                        updateSellData({ value: BigInt(0) })
                        return
                    } else {
                        // We have enough balance - subtract the gas cost from the max amount
                        setHasInsufficientGas(false)
                        maxAmount = maxAmount - totalCostInGasToken
                    }
                }

                updateSellData({ value: maxAmount })

                if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
                    updateBuyData({ value: maxAmount })
                }

                setIsInsufficientBalance(false)

                const formatted = new Balance(sellData.token)
                    .setValue(maxAmount)
                    .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)
                setSellAmountString(formatted)
            } catch (error) {
                console.error('[setMax] Error:', error)
                toast.error('Failed to set maximum amount')
                updateBuyData({ value: BigInt(0) })
            } finally {
                setIsBuyOutputLoading(false)
            }
        },
        [sellBalance, sellData, buyData, nativeBalance, wrappedNativeBalance]
    )

    /**
     * Refetch quote when sell amount changes
     */
    useEffect(() => {
        if (!isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData)) {
            if (sellData.value && sellData.value !== BigInt(0) && sellAmountString !== '0') {
                const qp = {
                    walletAddress: mainWalletAddress ?? '',
                    srcChain: getChainNameFromChainId(sellData.chainId),
                    dstChain: getChainNameFromChainId(buyData.chainId),
                    srcAssetAddress: sellData.token.address,
                    dstAssetAddress: buyData.token.address,
                    srcAmount: sellData.value,
                    targetAddress: destinationAddress || '',
                }
                invalidateQuoteAndRefetch(qp)
            }
        }
    }, [sellData.value])

    /**
     * Handle quote response changes
     */
    useEffect(() => {
        if (sellAmountString === '0') {
            // This means that the user deleted the sell amount string before the quote arrived.
            // See FE-578 in linear
            setIsBuyOutputLoading(false)
            updateBuyData({ value: BigInt(0) })
            return
        } else if (!isBuyOutputLoading && !isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData)) {
            if (quoteResponse.status === ResultCode.failure) {
                setIsBuyOutputLoading(false)
                updateBuyData({ value: BigInt(quoteResponse.quote?.dst_amount || 0) })
            } else if (
                quoteResponse.status === ResultCode.pending &&
                quoteResponse.errorMsg !== 'Uninitialized query call.'
            ) {
                setIsBuyOutputLoading(true)
                // logger.info('useEffect: setting isBuyOutputLoading to true.')
            } else {
                if (quoteResponse.quote && 'reject_order' in quoteResponse.quote) {
                    setLiquiditySource(quoteResponse.quote.liquidity_source as LiquiditySource)
                    setIsOrderRejected(quoteResponse.quote.reject_order)

                    if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
                        if (quoteResponse.quote.reject_order) {
                            setIsQuoteInvalid(true)
                            updateBuyData({ value: BigInt(quoteResponse.quote.dst_amount) })
                            setIsBuyOutputLoading(false)
                            return
                        }
                    } else if (quoteResponse.quote.reject_order) {
                        setIsQuoteInvalid(true)
                        updateBuyData({ value: BigInt(0) })
                        return
                    }

                    if (quoteResponse.quote.dst_asset_address?.toLowerCase() === buyData.token.address.toLowerCase()) {
                        updateBuyData({ value: BigInt(quoteResponse.quote.dst_amount) })
                        setIsQuoteInvalid(false)
                        setMachFee(extractMachFee(sellData, quoteResponse.quote))
                        setIsBuyOutputLoading(false)
                    } else {
                        if (buyData.token.native === true) {
                            // Check if we're using V2 API with 'native' token address
                            if (
                                isFeatureEnabled('V2_MIGRATION') &&
                                quoteResponse.quote.dst_asset_address === 'native'
                            ) {
                                // For V2 API, 'native' is the correct address for native tokens
                                updateBuyData({ value: BigInt(quoteResponse.quote.dst_amount) })
                                setIsQuoteInvalid(false)
                                setMachFee(extractMachFee(sellData, quoteResponse.quote))
                                setIsBuyOutputLoading(false)
                            } else if (
                                WRAPPED_GAS_TOKENS[buyData.chainId] &&
                                quoteResponse.quote.dst_asset_address.toLowerCase() ===
                                    WRAPPED_GAS_TOKENS[buyData.chainId].address.toLowerCase()
                            ) {
                                // For V1 API, wrapped token address is correct
                                updateBuyData({ value: BigInt(quoteResponse.quote.dst_amount) })
                                setIsQuoteInvalid(false)
                                setMachFee(extractMachFee(sellData, quoteResponse.quote))
                                setIsBuyOutputLoading(false)
                            } else if (!WRAPPED_GAS_TOKENS[buyData.chainId]) {
                                throw new Error(`no wrapped gas found for chainId ${buyData.chainId}`)
                            } else {
                                logger.warn('[useExchangeAmountLogic] Reloading quote due to address mismatch:', {
                                    quoteAddress: quoteResponse.quote.dst_asset_address,
                                    wrappedAddress: WRAPPED_GAS_TOKENS[buyData.chainId]?.address,
                                    buyToken: buyData.token,
                                    v2Enabled: isFeatureEnabled('V2_MIGRATION'),
                                })
                                refetchQuote()
                            }
                        } else {
                            logger.warn('[useExchangeAmountLogic] Reloading quote due to address mismatch:', {
                                quoteAddress: quoteResponse.quote.dst_asset_address,
                                buyToken: buyData.token,
                            })
                            refetchQuote()
                        }
                    }
                }
            }
        }
    }, [isBuyOutputLoading, quoteResponse, sellAmountString])

    // Fix for wrap/unwrap FE-451
    useEffect(() => {
        if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
            // logger.info('useEffect: setting buyData.value to sellData.value')
            updateBuyData({ value: sellData.value })
        }
    }, [sellData.token, buyData.token])

    // Check balance effect
    useEffect(() => {
        const amountIsCorrect = sellBalance !== undefined && sellBalance.bigintValue < sellData.value
        // logger.info('[useEffect] setting isInsufficientBalance to:', amountIsCorrect, `sellData=`, sellData)
        setIsInsufficientBalance(amountIsCorrect)
    }, [sellData, quoteResponse.quote, sellBalance])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            debouncedUpdateSellValue.cancel()
        }
    }, [])

    return {
        sellAmountString,
        setSellAmountString,
        isInsufficientBalance,
        isBuyOutputLoading,
        isQuoteInvalid,
        onChangeSellValue,
        handleManualRefresh,
        setMax,
        liquiditySource,
    }
}
